import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import CompanyIntakeForm from '../../components/event/CompanyIntakeForm';

export default function EventCompanyPage({ slug }) {
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordVerified, setPasswordVerified] = useState(false);

  useEffect(() => {
    const load = async () => {
      const { data } = await supabase
        .from('event_pages')
        .select('*')
        .eq('slug', slug)
        .eq('status', 'published')
        .single();
      setEvent(data);
      if (data && !data.event_password) setPasswordVerified(true);
      setLoading(false);
    };
    load();
  }, [slug]);

  if (loading) return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <div className="text-gray-400 text-sm">Loading...</div>
    </div>
  );

  if (!event) return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <div className="text-center">
        <h1 className="text-2xl font-bold text-gray-800 mb-2">Event Not Found</h1>
        <p className="text-gray-500">This event doesn't exist or hasn't been published yet.</p>
      </div>
    </div>
  );

  const formatDate = (d) => d ? new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '';

  // Password gate
  if (event.event_password && !passwordVerified) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-6">
        <div className="max-w-md w-full text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'EB Garamond, Georgia, serif' }}>{event.event_name}</h1>
          <p className="text-gray-500 mb-6">Enter the event password to continue.</p>
          <input
            type="password"
            value={passwordInput}
            onChange={e => setPasswordInput(e.target.value)}
            className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 text-sm focus:border-amber-500 focus:outline-none mb-4"
            placeholder="Event password"
          />
          <button
            onClick={() => {
              if (passwordInput === event.event_password) {
                setPasswordVerified(true);
              } else {
                alert('Incorrect password');
              }
            }}
            className="w-full px-6 py-3 rounded-full text-sm font-semibold text-white"
            style={{ background: 'linear-gradient(135deg, #c9a84c, #F59E0B)' }}
          >
            Enter
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)' }}>
        <div className="max-w-4xl mx-auto px-6 py-16 text-center">
          {event.logo_image_url && <img src={event.logo_image_url} alt="" className="h-12 mx-auto mb-6" />}
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'EB Garamond, Georgia, serif' }}>
            {event.company_headline || event.event_name}
          </h1>
          {event.company_value_prop && (
            <p className="text-lg text-gray-300 max-w-2xl mx-auto mb-6 leading-relaxed">{event.company_value_prop}</p>
          )}
          <div className="flex items-center justify-center gap-6 text-gray-300 text-sm">
            {event.start_date && (
              <span>📅 {formatDate(event.start_date)}{event.end_date ? ` — ${formatDate(event.end_date)}` : ''}</span>
            )}
            {event.time_commitment && <span>⏱ {event.time_commitment}</span>}
          </div>
        </div>
      </section>

      {/* Company Intake Form (includes sponsorship step + Calendly) */}
      <section className="max-w-3xl mx-auto px-6 py-16">
        <CompanyIntakeForm event={event} />
      </section>

      {/* Featured Inventors Teaser */}
      {event.featured_inventors?.length > 0 && event.featured_inventors[0]?.name && (
        <section className="bg-gray-50 py-16">
          <div className="max-w-4xl mx-auto px-6">
            <h2 className="text-2xl font-bold text-gray-900 text-center mb-8" style={{ fontFamily: 'EB Garamond, Georgia, serif' }}>Creators You'll Meet</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {event.featured_inventors.filter(i => i.name).map((inv, i) => (
                <div key={i} className="bg-white rounded-2xl p-5 shadow-sm text-center">
                  {inv.photo_url ? (
                    <img src={inv.photo_url} alt={inv.name} className="w-16 h-16 rounded-full object-cover mx-auto mb-3" />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center text-white text-xl font-bold mx-auto mb-3">
                      {inv.name.charAt(0)}
                    </div>
                  )}
                  <h3 className="font-semibold text-gray-900 text-sm">{inv.name}</h3>
                  {inv.title && <p className="text-xs text-amber-600">{inv.title}</p>}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="border-t border-gray-200 py-8 text-center">
        <p className="text-xs text-gray-400">
          Powered by <span className="font-semibold" style={{ color: '#c9a84c' }}>PlayVentr</span>
        </p>
      </footer>
    </div>
  );
}
